import { Controller } from "@hotwired/stimulus"

/**
 * Generalized data table controller with filtering, search, and bulk actions
 *
 * Usage:
 *   <div data-controller="data-table" data-data-table-bulk-action-url-value="/bulk/action">
 *     ...
 *   </div>
 */
export default class extends Controller {
  static targets = [
    "row",
    "selectAll",
    "bulkBar",
    "bulkCount",
    "searchInput",
    "filterButton"
  ]
  static values = {
    bulkActionUrl: String,
    defaultFilter: { type: String, default: "all" }
  }

  connect() {
    this.filterMode = this.defaultFilterValue
    this.applyFilters()
    this.updateCounts()
  }

  // Toggle individual row selection
  toggleRow(event) {
    this.updateBulkBar()
    this.updateSelectAll()
  }

  // Toggle all visible rows
  toggleAll(event) {
    const checked = event.target.checked
    this.visibleRows().forEach(row => {
      const checkbox = row.querySelector('input[type="checkbox"]')
      if (checkbox) checkbox.checked = checked
    })
    this.updateBulkBar()
  }

  // Search functionality (live filter without submitting parent form)
  search(event) {
    const query = event.target.value.toLowerCase().trim()
    this._filterRows(query)
  }

  preventSubmit(event) {
    if (event.key === "Enter") {
      event.preventDefault()
    }
  }

  // Filter by status or other criteria
  filter(event) {
    event.preventDefault()
    const button = event.currentTarget
    const newMode = button.dataset.filter

    // Update active button
    this.filterButtonTargets.forEach(btn => btn.classList.remove('active'))
    button.classList.add('active')

    this.filterMode = newMode
    this.applyFilters()
  }

  applyFilters() {
    const query = this.hasSearchInputTarget ?
      this.searchInputTarget.value.toLowerCase().trim() : ''
    this._filterRows(query)
  }

  _filterRows(query) {
    this.rowTargets.forEach(row => {
      const text = row.textContent.toLowerCase()
      const matchesSearch = query === '' || text.includes(query)
      const matchesFilter = this.matchesCurrentFilter(row)
      row.style.display = (matchesSearch && matchesFilter) ? '' : 'none'
    })
    this.updateBulkBar()
    this.updateSelectAll()
  }

  matchesCurrentFilter(row) {
    const status = row.dataset.status
    if (!status || this.filterMode === 'all') return true
    return this.filterMode === status
  }

  handleBulkAction(event) {
    this.processBulkAction(event)
  }

  // Specific bulk actions with defaults
  bulkRemove(event) {
    this.processBulkAction(event, {
      method: "DELETE",
      confirm: "Are you sure you want to remove the selected items? This cannot be undone."
    })
  }

  bulkForceReset(event) {
    this.processBulkAction(event, { method: "PATCH" })
  }

  processBulkAction(event, defaults = {}) {
    event.preventDefault() // Always prevent default here, as we're building a form
    event.stopPropagation()

    const link = event.currentTarget
    const url = link.href
    
    // Priority: data attribute > default > fallback
    // We check for undefined to allow empty strings (e.g. explicitly suppressing confirm) to override defaults
    const method = link.dataset.turboMethod !== undefined ? link.dataset.turboMethod : (defaults.method || "POST")
    const confirmMessage = link.dataset.turboConfirm !== undefined ? link.dataset.turboConfirm : defaults.confirm

    const selectedIds = this.selectedItemIds()

    if (selectedIds.length === 0) {
      this.showNotification("No items selected.", "warning")
      return
    }

    // Pass the confirm message to the submit function
    this.submitToUrl(url, method, selectedIds, confirmMessage)
  }



  submitToUrl(url, method, itemIds, confirmMessage) {
    const form = document.createElement('form')
    form.method = (method.toLowerCase() === 'get') ? 'GET' : 'POST'
    form.action = url
    form.style.display = 'none'

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    // Add method override if needed
    if (method.toUpperCase() !== 'POST' && method.toUpperCase() !== 'GET') {
      const methodInput = document.createElement('input')
      methodInput.type = 'hidden'
      methodInput.name = '_method'
      methodInput.value = method
      form.appendChild(methodInput)
    }

    // Add item IDs
    const idKey = this.getIdKey()
    itemIds.forEach(id => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = `${idKey}[]`
      input.value = id
      form.appendChild(input)
    })

    // Add data-turbo-confirm to the form if a message is present
    if (confirmMessage) {
      form.setAttribute('data-turbo-confirm', confirmMessage)
    }

    document.body.appendChild(form)
    // Dispatch a native submit event to let Turbo intercept
    form.requestSubmit()
  }

  showNotification(message, type = 'info') {
    // Create alert element
    const alertDiv = document.createElement('div')
    alertDiv.className = `alert alert-${type} alert-dismissible fade show`
    alertDiv.role = 'alert'
    alertDiv.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `

    // Insert at the top of the controller element
    this.element.prepend(alertDiv)

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      if (alertDiv) {
        alertDiv.classList.remove('show')
        setTimeout(() => alertDiv.remove(), 150) // Wait for fade transition
      }
    }, 3000)
  }

  // Helper methods
  visibleRows() {
    return this.rowTargets.filter(row => row.style.display !== 'none')
  }

  selectedItemIds() {
    return this.visibleRows()
      .filter(row => row.querySelector('input[type="checkbox"]')?.checked)
      .map(row => this.extractIdFromRow(row))
      .filter(id => id)
  }

  extractIdFromRow(row) {
    // Try common ID data attributes
    return row.dataset.enrollmentId ||
           row.dataset.userId ||
           row.dataset.itemId ||
           row.dataset.id ||
           row.id
  }

  getIdKey() {
    // Determine the parameter key based on what's in the first row
    const firstRow = this.rowTargets[0]
    if (!firstRow) return 'item_ids'

    if (firstRow.dataset.enrollmentId) return 'enrollment_ids'
    if (firstRow.dataset.userId) return 'user_ids'
    if (firstRow.dataset.itemId) return 'item_ids'
    return 'ids'
  }

  updateBulkBar() {
    const selectedCount = this.selectedItemIds().length

    if (this.hasBulkBarTarget) {
      if (selectedCount > 0) {
        this.bulkBarTarget.hidden = false
        if (this.hasBulkCountTarget) {
          this.bulkCountTarget.textContent = selectedCount
        }
      } else {
        this.bulkBarTarget.hidden = true
      }
    }
  }

  updateSelectAll() {
    if (!this.hasSelectAllTarget) return

    const visible = this.visibleRows()
    const checked = visible.filter(row =>
      row.querySelector('input[type="checkbox"]')?.checked
    )

    this.selectAllTarget.checked = visible.length > 0 &&
                                     visible.length === checked.length
    this.selectAllTarget.indeterminate = checked.length > 0 &&
                                          checked.length < visible.length
  }

  updateCounts() {
    // Can be overridden to update counts elsewhere in the UI
  }
}
